import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, UserCheck, UserX, AlertTriangle, Download, Filter, Users, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_MAP = {
  present: { label: 'Gəlib', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  late: { label: 'Gecikib', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  early_leave: { label: 'Erkən gedib', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  absent: { label: 'Gəlməyib', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

export default function HRPanel({ user }) {
  const [view, setView] = useState('today'); // today, history
  const [todayData, setTodayData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoModalOpen, setGeoModalOpen] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchToday = useCallback(async () => {
    try {
      let url = `${API}/hr/today`;
      if (selectedGroup !== 'all') url += `?group_id=${selectedGroup}`;
      const res = await axios.get(url, { headers });
      setTodayData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedGroup]);

  const fetchHistory = useCallback(async () => {
    try {
      let url = `${API}/hr/history?month=${selectedMonth}`;
      if (selectedUser !== 'all') url += `&user_id=${selectedUser}`;
      if (selectedGroup !== 'all') url += `&group_id=${selectedGroup}`;
      const res = await axios.get(url, { headers });
      setHistoryData(res.data);
    } catch (e) { console.error(e); }
  }, [selectedMonth, selectedUser, selectedGroup]);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/map/live`, { headers });
      setGroups(res.data?.groups || []);
    } catch (e) {}
  };

  useEffect(() => { fetchGroups(); }, []);
  useEffect(() => { if (view === 'today') fetchToday(); }, [view, fetchToday]);
  useEffect(() => { if (view === 'history') fetchHistory(); }, [view, fetchHistory]);

  const openGeoReport = async (userId, fullName, date) => {
    setGeoModalOpen(true);
    setGeoData({ user_id: userId, full_name: fullName, date, loading: true });
    setGeoLoading(true);
    try {
      const res = await axios.get(`${API}/hr/geofence-report?user_id=${userId}&date=${date}`, { headers });
      setGeoData({ ...res.data, full_name: fullName });
    } catch (e) {
      toast.error('Geofence hesabatı yüklənmədi');
      setGeoData(null);
    } finally {
      setGeoLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      let url = `${API}/hr/report/excel?month=${selectedMonth}`;
      if (selectedGroup !== 'all') url += `&group_id=${selectedGroup}`;
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `davamiyyet_${selectedMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('Hesabat yükləndi');
    } catch (e) { toast.error('Hesabat yüklənmədi'); }
  };

  const summary = todayData?.summary || {};

  return (
    <div className="space-y-4" data-testid="hr-panel">
      {/* Tabs + Filters */}
      <div className="bg-white rounded-lg border border-zinc-200 p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Button variant={view === 'today' ? 'default' : 'outline'} size="sm" onClick={() => setView('today')} className={view === 'today' ? 'bg-blue-600' : ''}>
            <UserCheck className="h-4 w-4 mr-1" /> Bu gün
          </Button>
          <Button variant={view === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setView('history')} className={view === 'history' ? 'bg-blue-600' : ''}>
            <Clock className="h-4 w-4 mr-1" /> Tarixçə
          </Button>

          {groups.length > 0 && (
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Bütün qruplar" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5} className="z-[9999] max-h-[300px] overflow-y-auto">
                <SelectItem value="all">Bütün qruplar</SelectItem>
                {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {view === 'history' && (
            <>
              <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-[160px]" />
              <Button variant="outline" size="sm" onClick={handleExportExcel}>
                <Download className="h-4 w-4 mr-1" /> Excel
              </Button>
            </>
          )}
        </div>

        {/* Summary */}
        {view === 'today' && todayData && (
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5 text-sm">
              <Users className="h-4 w-4 text-zinc-500" />
              <span>Cəm: <strong>{summary.total || 0}</strong></span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
              <span>Gəlib: <strong>{summary.present || 0}</strong></span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
              <span>Gecikib: <strong>{summary.late || 0}</strong></span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
              <span>Gəlməyib: <strong>{summary.absent || 0}</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* Today View */}
      {view === 'today' && todayData && (
        <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
          <div className="bg-zinc-800 px-4 py-3">
            <h3 className="text-sm font-bold text-white uppercase">Bu gün - {todayData.date}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Texnik</th>
                  <th className="px-4 py-2 text-center font-semibold">Status</th>
                  <th className="px-4 py-2 text-center font-semibold">Gəliş</th>
                  <th className="px-4 py-2 text-center font-semibold">Getmə</th>
                  <th className="px-4 py-2 text-center font-semibold">Gecikmə</th>
                  <th className="px-4 py-2 text-center font-semibold">İş saatı</th>
                  <th className="px-4 py-2 text-center font-semibold">Geofence</th>
                  <th className="px-4 py-2 text-left font-semibold">Ərazi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(todayData.records || []).map(r => {
                  const st = STATUS_MAP[r.status] || STATUS_MAP.absent;
                  return (
                    <tr key={r.user_id} className="hover:bg-zinc-50">
                      <td className="px-4 py-2 font-medium">{r.full_name}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center text-zinc-600">{r.check_in_time || '-'}</td>
                      <td className="px-4 py-2 text-center text-zinc-600">{r.check_out_time || '-'}</td>
                      <td className="px-4 py-2 text-center">
                        {r.late_minutes > 0 ? <span className="text-yellow-600 font-medium">{r.late_minutes} dəq</span> : '-'}
                      </td>
                      <td className="px-4 py-2 text-center font-medium">{r.work_hours ? `${r.work_hours}s` : '-'}</td>
                      <td className="px-4 py-2 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openGeoReport(r.user_id, r.full_name, todayData.date)}
                          className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`geo-btn-${r.user_id}`}
                        >
                          <MapPin className="h-3.5 w-3.5 mr-1" />
                          Bax
                        </Button>
                      </td>
                      <td className="px-4 py-2 text-xs text-zinc-500">{r.check_in_zone_name || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History View */}
      {view === 'history' && historyData && (
        <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
          <div className="bg-blue-700 px-4 py-3">
            <h3 className="text-sm font-bold text-white uppercase">Tarixçə - {historyData.month}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Tarix</th>
                  <th className="px-4 py-2 text-left font-semibold">Texnik</th>
                  <th className="px-4 py-2 text-center font-semibold">Status</th>
                  <th className="px-4 py-2 text-center font-semibold">Gəliş</th>
                  <th className="px-4 py-2 text-center font-semibold">Getmə</th>
                  <th className="px-4 py-2 text-center font-semibold">Gecikmə</th>
                  <th className="px-4 py-2 text-center font-semibold">İş saatı</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(historyData.records || []).map((r, i) => {
                  const st = STATUS_MAP[r.status] || STATUS_MAP.absent;
                  return (
                    <tr key={i} className="hover:bg-zinc-50">
                      <td className="px-4 py-2 text-zinc-600">{r.date}</td>
                      <td className="px-4 py-2 font-medium">{r.full_name}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">{r.check_in_time || '-'}</td>
                      <td className="px-4 py-2 text-center">{r.check_out_time || '-'}</td>
                      <td className="px-4 py-2 text-center">{r.late_minutes > 0 ? `${r.late_minutes} dəq` : '-'}</td>
                      <td className="px-4 py-2 text-center font-medium">{r.work_hours ? `${r.work_hours}s` : '-'}</td>
                    </tr>
                  );
                })}
                {(!historyData.records || historyData.records.length === 0) && (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-zinc-500">Bu dövr üçün məlumat yoxdur</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Geofence Report Modal */}
      <Dialog open={geoModalOpen} onOpenChange={setGeoModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="geofence-modal">
          <DialogHeader>
            <DialogTitle>Geofence Hesabatı</DialogTitle>
          </DialogHeader>
          {geoLoading || !geoData ? (
            <p className="text-zinc-500 text-center py-6">Yüklənir...</p>
          ) : (
            <div className="space-y-4">
              <div className="bg-zinc-50 rounded p-3 text-sm">
                <p><strong>Texnik:</strong> {geoData.full_name}</p>
                <p><strong>Tarix:</strong> {geoData.date}</p>
                <p><strong>Təyin olunan zonalar:</strong> {geoData.zones_count}</p>
                <p><strong>GPS nöqtələri (gün ərzində):</strong> {geoData.points_count}</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-zinc-500">Cəmi izlənmə</p>
                  <p className="text-2xl font-bold text-zinc-900">{geoData.total_minutes} dəq</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-emerald-600">Zona daxili</p>
                  <p className="text-2xl font-bold text-emerald-700">{geoData.in_zone_minutes} dəq</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600">Zona kənarı</p>
                  <p className="text-2xl font-bold text-red-700">{geoData.out_of_zone_minutes} dəq</p>
                </div>
              </div>

              {geoData.zones && geoData.zones.length > 0 && (
                <div>
                  <p className="font-medium text-sm mb-2">Zonalar üzrə bölgü</p>
                  <div className="space-y-1">
                    {geoData.zones.map(z => (
                      <div key={z.id} className="flex justify-between bg-emerald-50/50 px-3 py-2 rounded">
                        <span className="text-sm">{z.name}</span>
                        <span className="text-sm font-medium">{z.minutes} dəq</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {geoData.points_count === 0 && (
                <p className="text-zinc-400 italic text-center py-4">
                  Bu gün üçün GPS nöqtəsi qeyd edilməyib
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
